import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import {
  fixTimezoneOffsetYYYYMMDD,
  timerOptions,
} from '../../constants/constants';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import { IMultiSelectOption, IOption } from '../../models/ICommon';
import { IActiveTrayProcessDue } from '../../models/response/IActiveTrayResponse';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { IHKRDashboardDataVM } from '../../models/response/IHKRDashboardDataResponse';
import { IOrderTableViewListVM } from '../../models/response/IOrderTableViewResponse';
import { ITrayList, ITrayListVM } from '../../models/response/ITrayResponse';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllHKRDashboardDataState,
  GetAllOrderTableViewState,
  GetAllTrayState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
const appConfig = config();

export class DashboardStore {
  initialStateValue = ObservableInitialState;
  orderTableViewList: IOrderTableViewListVM = GetAllOrderTableViewState;
  getAllOrderTableViewState = { ...this.initialStateValue };
  getOrderTableListServiceState = { ...this.initialStateValue };
  orderList: ITrayListVM = GetAllTrayState;
  ledBlinkState = { ...this.initialStateValue };
  getHKRDashboardDataServiceState = { ...this.initialStateValue };
  hkrDashboardDataList: IHKRDashboardDataVM = GetAllHKRDashboardDataState;
  getColumnListServiceState = { ...this.initialStateValue };
  columnList: string[] = undefined;

  constructor() {
    makeObservable(this, {
      orderTableViewList: observable,
      getAllOrderTableViewState: observable,
      GetOrderTableViewListService: action,
      reset: action,
      allOrderTableView: computed,
      getOrderTableListServiceState: observable,
      orderList: observable,
      allColumnOptions: computed,
      LEDFormInitialValue: computed,
      TimerOptions: computed,
      ledBlinkState: observable,
      LedBlinkService: action,
      resetLedBlinkState: action,
      GetHKRDashboardDataService: action,
      getHKRDashboardDataServiceState: observable,
      hkrDashboardDataList: observable,
      allHKRDashboardData: computed,
      allProcesses: computed,
      resetGetOrderTableListServiceState: action,
      GetColumnListService: action,
      getColumnListServiceState: observable,
      columnList: observable,
      columnListDetails: computed,
      allProcessHeaders: computed,
      visibleHKRColumnsList: computed,
      visibleOrderTableColumnsList: computed,
    });
  }

  //#region Actions
  GetOrderTableViewListService = (
    TenantId: number,
    PageSize: number,
    PageNo: number,
    filters: string
  ) => {
    this.getOrderTableListServiceState.inProgress = true;
    const url = `${URLConstants.GetAllOrderTableViewList}?TenantId=${TenantId}&PageSize=${PageSize}&PageNo=${PageNo}&Filters=${filters}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayListVM>>) => {
        if (response.data.Error) {
          this.getOrderTableListServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.orderList = response.data.Data;
          this.getOrderTableListServiceState.success = true;
        }
      })
      .catch((err: string) => {
        this.getOrderTableListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getOrderTableListServiceState.inProgress = false;
        })
      );
  };

  LedBlinkService = (tenantId: number, trayId: string, data: any) => {
    this.ledBlinkState.inProgress = true;
    const url = `${URLConstants.LedBlink}?trayId=${trayId}&TenantId=${tenantId}&ColorCode=${encodeURIComponent(data.ColorCode)}&Second=${data.Second}`;
    return baseService
      .postRequest(url, null)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.ledBlinkState.error = response.data.Message;
        } else {
          this.ledBlinkState.success = true;
        }
      })
      .catch((err: string) => {
        this.ledBlinkState.error = err;
      })
      .finally(
        action(() => {
          this.ledBlinkState.inProgress = false;
        })
      );
  };

  GetHKRDashboardDataService = (
    TenantId: number,
    PageSize: number,
    PageNo: number,
    filters: string
  ) => {
    this.getHKRDashboardDataServiceState.inProgress = true;
    const url = `${URLConstants.HKRDashboardData}?TenantId=${TenantId}&PageSize=${PageSize}&PageNo=${PageNo}&Filters=${filters}&t=${new Date().getTime()}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IHKRDashboardDataVM>>) => {
          if (response.data.Error) {
            this.getHKRDashboardDataServiceState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.hkrDashboardDataList = response.data.Data;
            this.getHKRDashboardDataServiceState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getHKRDashboardDataServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getHKRDashboardDataServiceState.inProgress = false;
        })
      );
  };

  GetColumnListService = (TenantId: number) => {
    this.getColumnListServiceState.inProgress = true;
    const url = `${URLConstants.GetColumnList}/${TenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (response.data.Error) {
          this.getColumnListServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.columnList = response.data.Data;
          this.getColumnListServiceState.success = true;
        }
      })
      .catch((err: string) => {
        this.getColumnListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getColumnListServiceState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allOrderTableView(): ITrayList[] {
    let trayData: ITrayList[] = [];
    if (this.orderList?.Trays && this.orderList.Trays?.length > 0) {
      this.orderList.Trays.map(
        ({
          TrayId,
          CustomerId,
          Id,
          EndUserName,
          EndUserOrderId,
          OrderId,
          SubContractor,
          SubContractorName,
          ProductId,
          MaterialProductId,
          Katashiki1,
          Katashiki2,
          MaterialKatashiki,
          Kishu,
          ProcessName,
          Quantity,
          InvoiceNumber,
          QuantityUnitName,
          CustomerDueDate,
          MissingParts,
          PlanedDueDate,
          Group,
          ProductComment1,
          ProductComment2,
          ProductComment3,
          ProductComment4,
          ProductComment5,
          ProductComment6,
          CurrentState,
          Remarks,
          isSelected,
          ProductName,
          ActiveTrayProcessDue,
          NextProcess,
          PreviousProcess,
        }) => {
          const shortNameToDueDate = ActiveTrayProcessDue.reduce(
            (acc, process) => {
              acc[
                process.ProcessName.replace(/\s+/g, '') ||
                  `ProcessId_${process.ProcessId}`
              ] =
                process.DueDate !== '0001-01-01'
                  ? process.IsComplete
                    ? 'done'
                    : fixTimezoneOffsetYYYYMMDD(process.DueDate)
                  : 'N/A';
              return acc;
            },
            {}
          );
          trayData.push({
            TrayId,
            CustomerId,
            Id,
            EndUserName,
            EndUserOrderId,
            OrderId,
            SubContractor,
            SubContractorName,
            ProductId,
            MaterialProductId,
            Katashiki1,
            Katashiki2,
            MaterialKatashiki,
            Kishu,
            ProcessName,
            Quantity,
            InvoiceNumber,
            QuantityUnitName,
            MissingParts,
            ProductComment1,
            ProductComment2,
            ProductComment3,
            ProductComment4,
            ProductComment5,
            ProductComment6,
            Remarks,
            ProductName,
            Group,
            CurrentState,
            CustomerDueDate: fixTimezoneOffsetYYYYMMDD(CustomerDueDate),
            PlanedDueDate: fixTimezoneOffsetYYYYMMDD(PlanedDueDate),
            isSelected: false,
            ...shortNameToDueDate,
            NextProcess: NextProcess || 'N/A',
            PreviousProcess: PreviousProcess || 'N/A',
          });
        }
      );
    }
    return trayData;
  }

  get allProcessHeaders(): IActiveTrayProcessDue[] {
    const processHeaders = new Map<string, IActiveTrayProcessDue>();
    if (this.orderList?.Trays?.length > 0) {
      this.orderList.Trays.forEach(({ ActiveTrayProcessDue }) => {
        ActiveTrayProcessDue.forEach((item) => {
          const processIdKey = item.ProcessId.toString(); // Convert number to string
          if (!processHeaders.has(processIdKey)) {
            processHeaders.set(processIdKey, item);
          }
        });
      });
    }
    return Array.from(processHeaders.values());
  }

  get allColumnOptions(): IOption[] {
    const options: IOption[] = [
      {
        id: -1,
        value: 'please_select',
      },
    ];

    return options;
  }

  get LEDFormInitialValue(): any {
    return {
      ColorCode: '-1',
      Second: '10s',
    };
  }

  get TimerOptions(): IOption[] {
    return timerOptions;
  }

  get allHKRDashboardData() {
    let data = [];

    if (
      this.hkrDashboardDataList.HKRTableData &&
      this.hkrDashboardDataList.HKRTableData.length > 0
    ) {
      data = this.hkrDashboardDataList.HKRTableData.map((item, index) => {
        let processData = {};
        item.HKRProcessList.forEach((process) => {
          processData[process.ProcessShortName] = process.count;
        });

        return {
          Id: index + 1,
          Kishu: item.Kishu,
          ProcessDueDate: fixTimezoneOffsetYYYYMMDD(item.ProcessDueDate),
          Code: item.Code,
          Remaining: item.Remaining,
          ...processData,
        };
      });
    }

    return data;
  }

  get allProcesses() {
    if (
      this.hkrDashboardDataList.HKRTableData &&
      this.hkrDashboardDataList.HKRTableData.length > 0
    ) {
      return this.hkrDashboardDataList.HKRTableData[0].HKRProcessList.map(
        (process) => ({
          label: process.ProcessShortName,
          value: process.ProcessShortName,
        })
      );
    }
    return [];
  }

  get columnListDetails() {
    const list: IMultiSelectOption[] = [];
    if (this.columnList && this.columnList.length > 0) {
      this.columnList
        .filter((col) => col !== 'Planed Due Date') // Hide the Planed Due Date Column
        .map((column) =>
          list.push({
            // label: column.toLowerCase().replace(/\s+/g, '_'),
            // label: column.replace(/\s+/g, '_'),
            label: column,
            // value: column.replace(/ /g, ''),
            value: column
          })
        );
    }
    return list;
  }

  get visibleHKRColumnsList() {
    if (
      this.hkrDashboardDataList.SavedColumn &&
      this.hkrDashboardDataList.SavedColumn.length > 0
    ) {
      return this.hkrDashboardDataList.SavedColumn.split(',').map(
        (process) => ({
          label: process,
          value: process,
        })
      );
    }
    return [];
  }

  get visibleOrderTableColumnsList() {
    if (this.orderList.SavedColumn && this.orderList.SavedColumn.length > 0) {
      return this.orderList.SavedColumn.split(',').map((opt) => ({
        label: opt,
        value: opt,
      }));
    }
    return [];
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.orderTableViewList = GetAllOrderTableViewState;
  };

  resetGetOrderTableListServiceState = () => {
    this.getOrderTableListServiceState = { ...this.initialStateValue };
  };

  resetLedBlinkState = () => {
    this.ledBlinkState = { ...this.initialStateValue };
  };

  //#endregion
}

export default new DashboardStore();
